import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;
  #confirmedSelection = [];
  #requestNumber = 0;
  #confirmedMarkup = "";
  #changeHandler = this.variantChanged.bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.#rememberSelection();
    this.addEventListener('change', this.#changeHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#changeHandler);
    this.#abortController?.abort();
  }

  #rememberSelection(fromResponse = false) {
    const nodes = [...this.querySelectorAll('input[data-option-value-id], option[data-option-value-id]')];
    const text = this.querySelector('script[type="application/json"]')?.textContent || '';
    let selected = nodes.filter(node => node instanceof HTMLInputElement ? node.checked : node.selected);
    if (fromResponse) {
      try {
        const variant = JSON.parse(text);
        const groups = [...this.querySelectorAll('fieldset, select')].filter(group => group.querySelector('[data-option-value-id]'));
        const confirmed = groups.map((group, index) => [...group.querySelectorAll('[data-option-value-id]')].find(node => node.value === variant?.options?.[index]));
        if (confirmed.length && confirmed.every(Boolean)) selected = confirmed;
      } catch (_) {}
    }
    this.#confirmedSelection = selected.map(node => node.dataset.optionValueId);
    this.#confirmedMarkup = this.dataset.productId + '|' + text;
  }

  #syncConfirmedSelection() {
    const current = this.dataset.productId + '|' + (this.querySelector('script[type="application/json"]')?.textContent || '');
    if (current !== this.#confirmedMarkup) this.#rememberSelection(true);
  }

  #restoreSelection() {
    const selected = new Set(this.#confirmedSelection);
    this.querySelectorAll('input[data-option-value-id], option[data-option-value-id]').forEach(node => {
      const value = selected.has(node.dataset.optionValueId);
      if (node instanceof HTMLInputElement) node.checked = value;
      else { node.selected = value; node.toggleAttribute('selected', value); }
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.#syncConfirmedSelection();
    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    const request = this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);
    const number = this.#requestNumber;
    request.then(success => {
      if (success && number === this.#requestNumber && url.href !== window.location.href) history.replaceState({}, '', url.toString());
    });
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  async fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#syncConfirmedSelection();
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    const number = ++this.#requestNumber;
    try {
      const response = await fetch(requestUrl, { signal: controller.signal });
      if (!response.ok) throw new Error('Variant response ' + response.status);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (controller.signal.aborted || number !== this.#requestNumber) return false;
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
      const text = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (!text) throw new Error('Missing variant response');
      const variant = JSON.parse(text);
      if (variant !== null && (typeof variant !== 'object' || !variant.id)) throw new Error('Invalid variant response');
      this.#pendingRequestUrl = undefined;
      if (shouldMorphMain) this.updateMain(html);
      else {
        const source = html.querySelector(this.tagName.toLowerCase());
        const selected = source?.querySelector('select option[selected], fieldset input:checked')?.dataset.optionValueId;
        if (!selected) throw new Error('Missing selected variant option');
        const newProduct = this.updateVariantPicker(html);
        this.dispatchEvent(new VariantUpdateEvent(variant, selected, {
          html, productId: this.dataset.productId ?? '', newProduct,
        }));
        this.#rememberSelection();
      }
      return true;
    } catch (error) {
      if (controller.signal.aborted || number !== this.#requestNumber) return false;
      this.#pendingRequestUrl = undefined;
      this.#restoreSelection();
      this.dispatchEvent(new CustomEvent('variant:update:error', {
        bubbles: true,
        detail: { productId: this.dataset.productId ?? '', message: Theme.translations.variant_update_error || Theme.translations.recipient_form_error },
      }));
      return false;
    } finally {
      if (this.#abortController === controller) this.#abortController = undefined;
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

